import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Stack,
  Box,
  Fade,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { getPortfolioById, updateHolding } from "../../../api/portfolio_api";
import { fmtNumber } from "./portfolioHelpers";
import { getCurrencySymbol, fmtL } from "../../../utils/currency"; // ← adjust path to match your project

const EditSharesDialog = ({ open, onClose, portfolioId, holding, onSaved }) => {
  const [mode, setMode] = useState("buy");
  const [quantity, setQuantity] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [purchaseDate, setPurchaseDate] = useState("");

  useEffect(() => {
    if (open && holding) {
      setMode("buy");
      setQuantity("");
      setError("");
      const stockPrice = Number(holding.stock_current_price || holding.current_price || 0);
      setManualPrice(stockPrice.toFixed(2));
      const today = new Date().toISOString().split("T")[0];
      setPurchaseDate(today);
    }
  }, [holding, open]);

  if (!holding) return null;

  const sym = getCurrencySymbol(); // ← resolved once per render

  const originalShares = Number(holding.shares || 0);
  const originalTotalCost = Number(holding.cost_basis || 0);
  const originalCostPerShare = originalShares > 0 ? originalTotalCost / originalShares : 0;
  const currentStockPrice = Number(holding.stock_current_price || holding.current_price || 0);
  const qtyNum = parseFloat(quantity) || 0;

  const resultingShares =
    mode === "buy" ? originalShares + qtyNum :
      mode === "sell" ? originalShares - qtyNum :
        qtyNum;

  const tradePricePerShare =
    parseFloat(manualPrice) > 0 ? parseFloat(manualPrice) : currentStockPrice;

  let newTotalCostBasis;
  if (mode === "buy") {
    newTotalCostBasis = originalTotalCost + qtyNum * tradePricePerShare;
  } else {
    newTotalCostBasis = Math.max(originalTotalCost - qtyNum * originalCostPerShare, 0);
  }

  const newCostPerShare = resultingShares > 0 ? newTotalCostBasis / resultingShares : 0;
  const currentValue = resultingShares * currentStockPrice;

  const realizedGainLoss =
    mode === "sell" && qtyNum > 0
      ? qtyNum * tradePricePerShare - qtyNum * originalCostPerShare
      : 0;

  const hasChanged =
    qtyNum > 0 && resultingShares !== originalShares;

  const getValidationError = () => {
    if (mode === "sell" && qtyNum > originalShares)
      return `Cannot sell more than you own (${originalShares})`;
    if (resultingShares < 0) return "Resulting quantity cannot be negative";
    return "";
  };
  const validationError = getValidationError();

  const handleSave = async () => {
    if (resultingShares < 0) { setError("Resulting quantity cannot be negative"); return; }
    if (!hasChanged) { onClose(); return; }
    try {
      setSaving(true);
      setError("");
      await updateHolding(portfolioId, holding.ticker, {
        shares: resultingShares,
        cost_basis: Math.max(newTotalCostBasis, 0),
        purchase_date: purchaseDate || new Date().toISOString().split("T")[0],
      });
      const refreshed = await getPortfolioById(portfolioId);
      if (refreshed?.success) onSaved?.(refreshed.data);
      else onSaved?.();
    } catch {
      setError("Failed to update. Please try again.");
      setSaving(false);
      return;
    } finally {
      if (!error) { setSaving(false); onClose(); }
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !saving && !validationError) handleSave();
  };

  const modeConfig = {
    buy: {
      label: "Buy", icon: <AddCircleOutlineIcon sx={{ fontSize: 16 }} />,
      color: "var(--color-success)", bgActive: "rgba(16,185,129,0.12)",
      borderActive: "var(--color-success)", inputLabel: "Shares to buy",
      inputPrefix: "+", priceLabel: "Price per share (buy)",
      buttonLabel: `Buy ${qtyNum > 0 ? qtyNum : ""} shares`,
      buttonColor: "var(--color-success)", buttonHover: "#059669",
    },
    sell: {
      label: "Sell", icon: <RemoveCircleOutlineIcon sx={{ fontSize: 16 }} />,
      color: "var(--color-error)", bgActive: "rgba(239,68,68,0.12)",
      borderActive: "var(--color-error)", inputLabel: "Shares to sell",
      inputPrefix: "−", priceLabel: "Price per share (sell)",
      buttonLabel: `Sell ${qtyNum > 0 ? qtyNum : ""} shares`,
      buttonColor: "var(--color-error)", buttonHover: "#dc2626",
    },
  };
  const cfg = modeConfig[mode];

  const inputFieldSx = (accentColor, hasError = false) => ({
    fontSize: "var(--font-size-sm)",
    borderRadius: 1,
    bgcolor: "var(--color-bg-secondary)",
    color: "var(--color-text-primary)",
    "& fieldset": { borderColor: hasError ? "var(--color-error)" : "var(--color-border-primary)" },
    "&:hover fieldset": { borderColor: hasError ? "var(--color-error)" : "var(--color-border-secondary)" },
    "&.Mui-focused fieldset": { borderColor: hasError ? "var(--color-error)" : accentColor || "var(--color-primary)", borderWidth: "1px" },
  });

  return (
    <Dialog
      open={open} onClose={onClose} fullWidth maxWidth="md"
      PaperProps={{
        sx: {
          bgcolor: "var(--color-surface)", backgroundImage: "none",
          borderRadius: 1.5, border: "1px solid var(--color-border-primary)",
          boxShadow: "var(--shadow-xl)", m: { xs: 2, sm: 3 },
          maxHeight: { xs: "calc(100% - 32px)", sm: "calc(100% - 48px)" },
        },
      }}
    >
      {/* ── TITLE ── */}
      <DialogTitle sx={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        pb: 1.25, pt: 1.75, px: 2.5,
        borderBottom: "1px solid var(--color-border-primary)",
      }}>
        <Box display="flex" alignItems="center" gap={1.25}>
          <Box sx={{
            width: 32, height: 32, borderRadius: 1,
            bgcolor: "var(--color-surface-hover)",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <EditIcon sx={{ fontSize: 18, color: "var(--color-primary)" }} />
          </Box>
          <Typography variant="tableHeader" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">
            Configure Holdings
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" disabled={saving}
          sx={{ color: "var(--color-text-muted)", p: 0.5, "&:hover": { bgcolor: "var(--color-surface-hover)" } }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, py: 2.5, mt: 2 }}>
        <Stack spacing={2.5}>

          {/* ── STOCK INFO ── */}
          <Box sx={{
            bgcolor: "var(--color-bg-secondary)", borderRadius: 1,
            p: 1.75, border: "1px solid var(--color-border-primary)",
          }}>
            <Typography variant="chipText" fontWeight="var(--font-weight-bold)" color="var(--color-primary)" mb={0.5} display="block">
              {holding.ticker}
            </Typography>
            <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)" lineHeight={1.5}>
              {holding.company_name}
            </Typography>
            {[
              { label: "Market Price",      value: `${sym}${fmtNumber(currentStockPrice)}`,   color: "var(--color-success)" },
              { label: "Current Quantity",  value: fmtL(originalShares, 2) },
              { label: "Avg Cost / Share",  value: `${sym}${fmtNumber(originalCostPerShare)}` },
              { label: "Total Cost Basis",  value: `${sym}${fmtNumber(originalTotalCost)}` },
            ].map(({ label, value, color }, i) => (
              <Box key={i} sx={{
                display: "flex", justifyContent: "space-between", alignItems: "center",
                mt: i === 0 ? 1.5 : 0.75,
                ...(i === 0 && { pt: 1.5, borderTop: "1px solid var(--color-border-primary)" }),
              }}>
                <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                  {label}
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color={color || "var(--color-text-primary)"}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>

          {/* ── MODE TOGGLE ── */}
          <Box>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={1}>
              Action
            </Typography>
            <ToggleButtonGroup
              value={mode} exclusive fullWidth
              onChange={(_, newMode) => {
                if (newMode !== null) {
                  const today = new Date().toISOString().split("T")[0];
                  setMode(newMode);
                  setQuantity("");
                  setManualPrice(currentStockPrice.toFixed(2));
                  setPurchaseDate(today);
                  setError("");
                }
              }}
              sx={{
                height: 38,
                "& .MuiToggleButton-root": {
                  textTransform: "none",
                  fontSize: "var(--font-size-sm)",
                  fontWeight: "var(--font-weight-semibold)",
                  border: "1px solid var(--color-border-primary)",
                  color: "var(--color-text-secondary)", gap: 0.75, py: 0.5,
                  "&:hover": { bgcolor: "var(--color-surface-hover)" },
                },
              }}
            >
              {["buy", "sell"].map((m) => (
                <ToggleButton key={m} value={m} sx={{
                  "&.Mui-selected": {
                    bgcolor: modeConfig[m].bgActive,
                    color: modeConfig[m].color,
                    borderColor: modeConfig[m].borderActive,
                    "&:hover": { bgcolor: modeConfig[m].bgActive },
                  },
                }}>
                  {modeConfig[m].icon}&nbsp;{modeConfig[m].label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {/* ── QUANTITY INPUT ── */}
          <Box>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
              {cfg.inputLabel}
            </Typography>
            <TextField
              fullWidth size="small" type="text" placeholder="0"
              value={quantity}
              onChange={(e) => {
                const v = e.target.value;
                if (v === "" || /^\d*\.?\d*$/.test(v)) { setQuantity(v); setError(""); }
              }}
              onKeyPress={handleKeyPress}
              error={!!error || !!validationError}
              helperText={error || validationError}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color={cfg.color}>
                      {cfg.inputPrefix}
                    </Typography>
                  </InputAdornment>
                ),
                sx: inputFieldSx(cfg.color, !!error || !!validationError),
              }}
              FormHelperTextProps={{ sx: { mx: 0, mt: 0.75, fontSize: "var(--font-size-xs)", color: "var(--color-error)" } }}
              autoFocus
            />
            {/* Quick qty presets */}
            <Box display="flex" gap={0.75} mt={1}>
              {(mode === "sell"
                ? [1, 5, 10, Math.floor(originalShares / 2), originalShares]
                  .filter((v, i, a) => v > 0 && v <= originalShares && a.indexOf(v) === i)
                : [1, 5, 10, 25, 50]
              ).slice(0, 5).map((val) => (
                <Button key={val} size="small"
                  onClick={() => { setQuantity(val.toString()); setError(""); }}
                  sx={{
                    minWidth: 0, flex: 1,
                    fontSize: "var(--font-size-xs)",
                    fontWeight: "var(--font-weight-semibold)",
                    textTransform: "none", py: 0.35, borderRadius: 0.75,
                    border: "1px solid",
                    color: parseFloat(quantity) === val ? cfg.color : "var(--color-text-secondary)",
                    bgcolor: parseFloat(quantity) === val ? cfg.bgActive : "transparent",
                    borderColor: parseFloat(quantity) === val ? cfg.borderActive : "var(--color-border-primary)",
                    "&:hover": { bgcolor: cfg.bgActive, borderColor: cfg.borderActive, color: cfg.color },
                  }}
                >
                  {mode === "sell" && val === originalShares ? "All" : val}
                </Button>
              ))}
            </Box>
          </Box>

          {/* ── PRICE INPUT ── */}
          <Box>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
              {cfg.priceLabel}
            </Typography>
            <TextField
              fullWidth size="small" type="text"
              value={manualPrice}
              onChange={(e) => {
                const v = e.target.value.replace(/,/g, "");
                if (v === "" || /^\d*\.?\d*$/.test(v)) setManualPrice(v);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Typography fontSize="var(--font-size-sm)" color="var(--color-text-muted)">{sym}</Typography>
                  </InputAdornment>
                ),
                sx: inputFieldSx(cfg.color),
              }}
            />
            <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" mt={0.5}>
              {mode === "sell"
                ? `Avg cost/share is ${sym}${fmtNumber(originalCostPerShare)} — sell price used only for realized G/L`
                : `Defaults to market price ${sym}${fmtNumber(currentStockPrice)}. Override if your trade price differs.`}
            </Typography>
          </Box>

          {/* ── TRANSACTION DATE ── */}
          <Box>
            <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-medium)" color="var(--color-text-primary)" mb={0.75}>
              Transaction date
            </Typography>
            <TextField
              fullWidth size="small" type="date"
              value={purchaseDate}
              onChange={(e) => setPurchaseDate(e.target.value)}
              InputProps={{ sx: inputFieldSx("var(--color-primary)") }}
              inputProps={{ max: new Date().toISOString().split("T")[0] }}
            />
          </Box>

          {/* ── COST BASIS BREAKDOWN ── */}
          {qtyNum > 0 && (
            <Box sx={{ bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.75, border: "1px solid var(--color-border-primary)" }}>
              <Typography
                fontSize="var(--font-size-xs)" fontWeight="var(--font-weight-semibold)"
                color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.05em"
                mb={1.25} display="block"
              >
                Cost Basis Calculation
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                  Existing total cost
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-primary)">
                  {sym}{fmtNumber(originalTotalCost)}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
                <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                  {mode === "buy"
                    ? `Buy (${qtyNum} × ${sym}${fmtNumber(tradePricePerShare)})`
                    : `Remove (${qtyNum} × ${sym}${fmtNumber(originalCostPerShare)} avg)`}
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)"
                  color={mode === "buy" ? "var(--color-success)" : "var(--color-error)"}>
                  {mode === "buy"
                    ? `+${sym}${fmtNumber(qtyNum * tradePricePerShare)}`
                    : `−${sym}${fmtNumber(qtyNum * originalCostPerShare)}`}
                </Typography>
              </Box>

              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, pt: 1.25, borderTop: "1px solid var(--color-border-primary)" }}>
                <Typography fontSize="var(--font-size-xs)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
                  New total cost basis
                </Typography>
                <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-bold)" color="var(--color-text-primary)">
                  {sym}{fmtNumber(Math.max(newTotalCostBasis, 0))}
                </Typography>
              </Box>

              {resultingShares > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75 }}>
                  <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                    New avg cost / share
                  </Typography>
                  <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)" color="var(--color-text-secondary)">
                    {sym}{fmtNumber(newCostPerShare)}
                  </Typography>
                </Box>
              )}

              {mode === "sell" && qtyNum > 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 0.75, pt: 0.75, borderTop: "1px solid var(--color-border-primary)" }}>
                  <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)" textTransform="uppercase" letterSpacing="0.03em">
                    Realized gain / loss
                  </Typography>
                  <Typography fontSize="var(--font-size-sm)" fontWeight="var(--font-weight-semibold)"
                    color={realizedGainLoss >= 0 ? "var(--color-success)" : "var(--color-error)"}>
                    {realizedGainLoss >= 0 ? "+" : "−"}{sym}{fmtNumber(Math.abs(realizedGainLoss))}
                  </Typography>
                </Box>
              )}
            </Box>
          )}

          {/* ── RESULT SUMMARY ── */}
          <Fade in timeout={300}>
            <Box sx={{
              bgcolor: "var(--color-bg-secondary)", borderRadius: 1, p: 1.75,
              border: `1px solid ${qtyNum > 0 ? cfg.color : "var(--color-border-secondary)"}`,
            }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Typography fontSize="var(--font-size-xs)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
                  Resulting shares
                </Typography>
                <Box display="flex" alignItems="center" gap={1}>
                  {qtyNum > 0 && (
                    <Typography fontSize="var(--font-size-xs)" color="var(--color-text-muted)">
                      {originalShares} {mode === "buy" ? "+" : "−"} {qtyNum} =
                    </Typography>
                  )}
                  <Typography
                    fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)"
                    color={resultingShares < 0 ? "var(--color-error)" : "var(--color-text-primary)"}
                  >
                    {resultingShares < 0 ? "Invalid" : fmtL(resultingShares, 2)}
                  </Typography>
                </Box>
              </Box>
              {resultingShares >= 0 && (
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 1.25, pt: 1.25, borderTop: "1px solid var(--color-border-secondary)" }}>
                  <Typography fontSize="var(--font-size-xs)" color="var(--color-text-secondary)" textTransform="uppercase" letterSpacing="0.03em">
                    New market value
                  </Typography>
                  <Typography fontSize="var(--font-size-base)" fontWeight="var(--font-weight-semibold)" color="var(--color-success)">
                    {sym}{fmtNumber(currentValue)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Fade>

        </Stack>
      </DialogContent>

      {/* ── ACTIONS ── */}
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1, borderTop: "1px solid var(--color-border-primary)" }}>
        <Button onClick={onClose} disabled={saving} fullWidth
          sx={{
            textTransform: "none", fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-medium)", py: 0.65,
            color: "var(--color-text-secondary)",
            "&:hover": { bgcolor: "var(--color-surface-hover)" },
          }}>
          Cancel
        </Button>
        <Button
          variant="contained" onClick={handleSave}
          disabled={saving || !hasChanged || !!validationError || resultingShares < 0}
          fullWidth
          sx={{
            textTransform: "none", fontSize: "var(--font-size-sm)",
            fontWeight: "var(--font-weight-semibold)", py: 0.65,
            bgcolor: cfg.buttonColor, boxShadow: "none",
            "&:hover": { bgcolor: cfg.buttonHover, boxShadow: "none" },
            "&.Mui-disabled": { bgcolor: "var(--color-surface-hover)", color: "var(--color-text-muted)" },
          }}>
          {saving ? <CircularProgress size={16} color="inherit" /> : cfg.buttonLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditSharesDialog;